/**
 * Shared refresh/purchase/connect action button for all import entry points.
 * Matches dashboard Credit Scores action styling.
 */

import React from 'react';
import { RefreshCw, Loader2, FileText } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import { useProviderRefreshAction } from '../../hooks/useProviderRefreshAction';
import { useProviderRefreshImport } from '../../hooks/useProviderRefreshImport';
import {
  getRefreshActionLabel,
  getRefreshActionSubtext,
  formatLastUpdatedMmDdYy,
  REFRESH_STATUS_UNAVAILABLE_MESSAGE,
} from '../../utils/providerRefreshAction';

const ProviderRefreshActionButton = ({
  provider,
  lastUpdated = null,
  hasSavedReport = false,
  theme = null,
  enabled = true,
  className = '',
  onImportStarted,
  onNeedCredentials,
  onViewReport,
  onRefreshMetadataUpdated,
  showUnavailableNotice = true,
  'data-testid': dataTestId = 'provider-refresh-action-btn',
}) => {
  const { t } = useI18n();
  const {
    actionState,
    hasCredentials,
    loading,
    statusUnavailable,
    refetch,
  } = useProviderRefreshAction(provider, {
    enabled,
    hasSavedReport,
  });

  const { importing, executeAction } = useProviderRefreshImport({
    provider,
    actionState,
    hasCredentials,
    onImportStarted: (importId, importData) => {
      onImportStarted?.(importId, importData);
      refetch();
    },
    onNeedCredentials,
    onViewReport,
    onRefreshMetadataUpdated: () => {
      onRefreshMetadataUpdated?.();
      refetch();
    },
  });

  if (!provider || !enabled) return null;

  const label = getRefreshActionLabel(actionState, t);
  const subtext = getRefreshActionSubtext(actionState, { lastUpdated, t })
    || (lastUpdated && actionState?.action === 'refresh_now'
      ? t('creditReport.lastUpdatedOn', { date: formatLastUpdatedMmDdYy(lastUpdated) })
      : null);

  const isPrimarySolid = false;
  const accent = theme?.accent || '#2563EB';
  const isDark = theme?.isDark;

  const handleClick = async () => {
    if (importing || loading) return;
    if (statusUnavailable) {
      await refetch();
    }
    executeAction();
  };

  const isViewReport = actionState?.action === 'view_report';

  return (
    <div className="inline-flex flex-col items-stretch gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={importing || loading}
        className={`inline-flex flex-col items-center justify-center px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-200 min-w-[140px] ${className}`}
        style={
          isPrimarySolid
            ? {
                background: accent,
                color: '#fff',
                border: `1px solid ${accent}`,
                boxShadow: isDark
                  ? '0 8px 24px rgba(0,0,0,0.25)'
                  : '0 8px 20px rgba(37, 99, 235, 0.25)',
              }
            : {
                background: isDark ? 'rgba(255,255,255,0.04)' : '#fff',
                color: accent,
                border: `1.5px solid ${accent}`,
              }
        }
        data-testid={dataTestId}
      >
        <span className="flex items-center gap-2">
          {importing || loading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : isViewReport ? (
            <FileText size={16} />
          ) : (
            <RefreshCw size={16} />
          )}
          <span>{label}</span>
        </span>
        {subtext && (
          <span
            className="text-[10px] font-normal mt-0.5 opacity-75"
            style={{ color: isPrimarySolid ? 'rgba(255,255,255,0.85)' : undefined }}
          >
            {subtext}
          </span>
        )}
      </button>
      {showUnavailableNotice && statusUnavailable && !loading && (
        <p
          className="text-[10px] text-center leading-snug opacity-80"
          style={{ color: theme?.text?.muted || '#64748b' }}
          data-testid="refresh-status-unavailable-notice"
        >
          {REFRESH_STATUS_UNAVAILABLE_MESSAGE}
        </p>
      )}
    </div>
  );
};

export default ProviderRefreshActionButton;
